/**
 * TIER 1: Branded Types for Type Safety
 *
 * These branded types provide compile-time type safety for FHIR resource IDs,
 * codes, and other domain-specific values to prevent mixing incompatible types.
 * @agentic @benchmark/core
 */ /**
 * Base branded type for compile-time type safety.
 * @agentic @benchmark/core
 */ /**
 * Internal helper to create a validated resource ID.
 * @agentic @benchmark/core
 */ const createResourceId = (resourceType, id)=>{
    if (!id || typeof id !== "string") {
        throw new Error(`Invalid ID for ${resourceType}: ${id}`);
    }
    return id;
};
/**
 * Converts a string to a type-safe PatientId.
 * @agentic @benchmark/core
 */ const toPatientId = (id)=>createResourceId("Patient", id);
/**
 * Converts a string to a type-safe PractitionerId.
 * @agentic @benchmark/core
 */ const toPractitionerId = (id)=>createResourceId("Practitioner", id);
/**
 * Converts a string to a type-safe OrganizationId.
 * @agentic @benchmark/core
 */ const toOrganizationId = (id)=>createResourceId("Organization", id);
/**
 * Coerces a string into a branded {@link FhirDateTime}. Guards against empty or
 * non-string input so call sites stop reaching for `as FhirDateTime`.
 * @agentic @benchmark/core
 */ const toFhirDateTime = (value)=>{
    if (!value || typeof value !== "string") {
        throw new Error(`Invalid FhirDateTime: ${value}`);
    }
    return value;
};

/**
 * FHIR Coverage.order for ranking when multiple coverages are active (lower = higher applicability).
 * @agentic @benchmark/core
 */ const COVERAGE_APPLICABILITY_PRIMARY = 1;
/**
 * Second applicability tier when two active insurances are on file.
 * @agentic @benchmark/core
 */ const COVERAGE_APPLICABILITY_SECONDARY = 2;

/**
 * Coverage Area form-data type for HealthcareService geographic coverage.
 *
 * Backed by canonical state-coverage `Location` resources resolved at
 * action time (see `services/coverage-area.service.ts`). The form
 * surface (Connections) only needs to know the state code; the
 * `locationId` / `locationDisplay` slots are populated by adapters
 * and the action layer once the canonical Location is resolved.
 *
 * @agentic @benchmark/core
 */ /**
 * Returns true when a CoverageAreaFormData entry has been resolved to
 * a canonical `Location` reference (i.e., `locationId` is populated).
 * @agentic @benchmark/core
 */ const isResolvedCoverageArea = (area)=>typeof area.locationId === "string" && area.locationId.length > 0;

/**
 * Location form data for inline creation within HealthcareService form.
 * NOTE: Used for PROVIDER/CLINIC locations only. Do NOT use for patient addresses (PHI).
 * @fhir-resource Location
 * @agentic @benchmark/core
 */ /**
 * Checks if LocationFormData represents a new location to be created.
 * @agentic @benchmark/core
 */ function isNewLocation(location) {
    return location.isNew && !location.existingId;
}
/**
 * Checks if LocationFormData has valid address data.
 * @agentic @benchmark/core
 */ function hasValidAddress(location) {
    return Boolean(location.street && location.city && location.state && location.zip);
}
/**
 * Checks if LocationFormData has valid geographic coordinates.
 * @agentic @benchmark/core
 */ function hasValidCoordinates(location) {
    return typeof location.latitude === "number" && typeof location.longitude === "number" && location.latitude >= -90 && location.latitude <= 90 && location.longitude >= -180 && location.longitude <= 180;
}

/**
 * Program Selection Form Data Types
 * @agentic @benchmark/core
 */ /**
 * Creates a blank service need form item with a unique identifier.
 * @agentic @benchmark/core
 */ const createEmptyServiceNeedFormItem = ()=>({
        id: crypto.randomUUID(),
        serviceType: "",
        modes: [],
        conditions: [],
        preferences: ""
    });

/**
 * Provider Organization form data for inline creation within HealthcareService form.
 * @agentic @benchmark/core
 */ /**
 * Checks if ProviderOrganizationFormData represents a new organization to be created.
 * @agentic @benchmark/core
 */ function isNewProviderOrganization(organization) {
    return organization.isNew && !organization.existingId;
}

export { COVERAGE_APPLICABILITY_PRIMARY, COVERAGE_APPLICABILITY_SECONDARY, createEmptyServiceNeedFormItem, hasValidAddress, hasValidCoordinates, isNewLocation, isNewProviderOrganization, isResolvedCoverageArea, toFhirDateTime, toOrganizationId, toPatientId, toPractitionerId };
